import { useCallback, useContext, useMemo } from 'react';
import {
  checkIsSuperAdmin,
  checkIsAdministrator,
  checkIsRegularAdmin,
} from 'shared/lib/authUtils';
import { PermissionContext } from './PermissionProvider';
import { resolveAccess } from './resolve-access';

/**
 * usePermissions — aggregate accessor for the Permission Access Layer
 * (SA-1592 Phase A0).
 *
 * Exposes a `resolve` function returning the raw resolved menu object (the
 * full bag, for callers that need fields beyond the CRUD flags exposed by
 * `usePermission`) plus the current user's role booleans. Role booleans are
 * sourced from `authUtils` and memoized; they read decrypted localStorage and
 * are stable for the life of the render unless the projection changes.
 *
 * @returns {{ resolve: (keyOrPathname: string) => object|null,
 *   isSuperAdmin: boolean, isAdministrator: boolean, isRegularAdmin: boolean }}
 */
export const usePermissions = () => {
  const { menus } = useContext(PermissionContext);

  const resolve = useCallback(
    (keyOrPathname) => resolveAccess(menus, keyOrPathname),
    [menus]
  );

  // Intentional dep on [menus], not on the role functions' (localStorage)
  // inputs: the role checkers read the `role` key directly, but a role change
  // never happens in isolation — `applyPermissionsFromMe` rewrites `menus` in
  // the same pass and the provider re-projects `menus` on `permissions:updated`
  // / `storage`. So `[menus]` is the correct reactive trigger to re-read the
  // role. The checkers are module-scope stable (no exhaustive-deps violation).
  const roles = useMemo(
    () => ({
      isSuperAdmin: checkIsSuperAdmin(),
      isAdministrator: checkIsAdministrator(),
      isRegularAdmin: checkIsRegularAdmin(),
    }),
    [menus]
  );

  return { resolve, ...roles };
};
